import { fetchHistoricalCandles } from './CryptoDataStore';

// Lightweight IndexedDB-backed ledger tracking when tokens were first seen/shared
// All data stays on the client device — no server needed

const DB_NAME = 'ape_token_ledger';
const DB_VERSION = 1;
const STORE_FIRST_SEEN = 'firstSeen';
const STORE_MENTIONS = 'mentions';

export interface FirstSeenEntry {
  tokenKey: string;
  altKey?: string;
  timestamp: number;
  mcAtSeen: number;
  priceAtSeen: number;
  chatTitle?: string;
  chatId?: string;
  senderName?: string;
  senderId?: string;
  messageId?: number;
  historyStartMcap?: number;
  historyMaxMcap?: number;
  historyMinMcap?: number;
  historyVolume?: number;
}

export interface MentionEntry {
  id?: number; // Auto-incremented
  tokenKey: string;
  altKey?: string;
  timestamp: number;
  mcAtMention: number;
  priceAtMention: number;
  chatTitle?: string;
  chatId?: string;
  senderName?: string;
  senderId?: string;
  messageId?: number;
  messageDate?: number;
  historyStartMcap?: number;
  historyMaxMcap?: number;
  historyMinMcap?: number;
  historyVolume?: number;
}

let dbInstance: IDBDatabase | undefined;

function openDb(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_FIRST_SEEN)) {
        db.createObjectStore(STORE_FIRST_SEEN, { keyPath: 'tokenKey' });
      }
      if (!db.objectStoreNames.contains(STORE_MENTIONS)) {
        const mentionStore = db.createObjectStore(STORE_MENTIONS, { keyPath: 'id', autoIncrement: true });
        mentionStore.createIndex('byToken', 'tokenKey', { unique: false });
        mentionStore.createIndex('bySender', 'senderId', { unique: false });
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onerror = () => reject(request.error);
  });
}

// Record the first time we see a token (only writes if not already stored, or if older timestamp found)
export async function recordFirstSeen(
  tokenKey: string,
  mcAtSeen: number,
  priceAtSeen: number,
  context?: {
    chatTitle?: string;
    chatId?: string;
    senderName?: string;
    senderId?: string;
    messageDate?: number;
    messageId?: number;
    chain?: string;
    mintAddress?: string;
    altKey?: string;
  },
  forceOverride?: boolean,
): Promise<boolean> {
  const messageDateMs = context?.messageDate ? context.messageDate * 1000 : undefined;
  const db = await openDb();
  const existing = await getFirstSeen(tokenKey, context?.altKey);

  if (existing) {
    if (!forceOverride) {
      // If an existing record exists, only overwrite if current message is STRICTLY OLDER
      if (messageDateMs && messageDateMs < existing.timestamp) {
        // Proceed with older message overwrite
      } else {
        return false;
      }
    }
  }

  // Use the on-chain mint address for historical candle lookups (ticker symbols fail on candle APIs)
  const candleTarget = context?.mintAddress || (tokenKey.length >= 32 ? tokenKey : undefined);
  const history = candleTarget
    ? await fetchHistoricalCandles(candleTarget, context?.chain || 'sol', messageDateMs, priceAtSeen)
    : { startPrice: priceAtSeen, maxPrice: priceAtSeen, minPrice: priceAtSeen };

  let hStart = 0;
  let hMax = 0;
  let hMin = 0;

  if (messageDateMs && Date.now() - messageDateMs < 300000) {
    // If it's a recent call (< 5 mins), just use the live MCAP
    hStart = mcAtSeen;
    hMax = mcAtSeen;
    hMin = mcAtSeen;
  } else if (history.startPrice && history.startPrice > 0 && priceAtSeen > 0) {
    const supply = mcAtSeen / priceAtSeen;
    hStart = history.startPrice * supply;
    hMax = (history.maxPrice && history.maxPrice > 0) ? history.maxPrice * supply : hStart;
    hMin = (history.minPrice && history.minPrice > 0) ? history.minPrice * supply : hStart;
  }

  const entry: FirstSeenEntry = {
    tokenKey,
    altKey: context?.altKey,
    timestamp: messageDateMs || Date.now(),
    mcAtSeen: hStart,
    priceAtSeen: history.startPrice || priceAtSeen,
    chatTitle: context?.chatTitle,
    chatId: context?.chatId,
    senderName: context?.senderName,
    senderId: context?.senderId,
    messageId: context?.messageId,
    historyStartMcap: hStart,
    historyMaxMcap: hMax,
    historyMinMcap: hMin,
    historyVolume: history.volume,
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_FIRST_SEEN, 'readwrite');
    const store = tx.objectStore(STORE_FIRST_SEEN);
    store.put(entry);

    // Also write alias link under altKey (e.g., $SYMBOL <-> mint)
    if (context?.altKey && context.altKey !== tokenKey) {
      const altEntry: FirstSeenEntry = {
        ...entry,
        tokenKey: context.altKey,
        altKey: tokenKey,
      };
      store.put(altEntry);
    }

    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

// Get first-seen data for a token (checks both primary key and alias key)
export async function getFirstSeen(tokenKey: string, altKey?: string): Promise<FirstSeenEntry | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_FIRST_SEEN, 'readonly');
    const store = tx.objectStore(STORE_FIRST_SEEN);
    const req1 = store.get(tokenKey);
    let req2: IDBRequest<FirstSeenEntry | undefined> | undefined;

    if (altKey && altKey !== tokenKey) {
      req2 = store.get(altKey);
    }

    tx.oncomplete = () => {
      const res1 = req1.result as FirstSeenEntry | undefined;
      const res2 = req2?.result;

      if (res1 && res2) {
        resolve(res1.timestamp <= res2.timestamp ? res1 : res2);
      } else {
        resolve(res1 || res2 || undefined);
      }
    };
    tx.onerror = () => reject(tx.error);
  });
}

// Record a mention (every chat where the token appeared)
export async function recordMention(
  tokenKey: string,
  mcAtMention: number,
  priceAtMention: number,
  context?: {
    chatTitle?: string;
    chatId?: string;
    senderName?: string;
    senderId?: string;
    messageDate?: number;
    messageId?: number;
    chain?: string;
    mintAddress?: string;
    altKey?: string;
  },
): Promise<void> {
  const messageDateMs = context?.messageDate ? context.messageDate * 1000 : undefined;
  const db = await openDb();

  const candleTarget = context?.mintAddress || (tokenKey.length >= 32 ? tokenKey : undefined);
  const history = candleTarget
    ? await fetchHistoricalCandles(candleTarget, context?.chain || 'sol', messageDateMs, priceAtMention)
    : { startPrice: priceAtMention, maxPrice: priceAtMention, minPrice: priceAtMention };

  let hStart = mcAtMention;
  let hMax = mcAtMention;
  let hMin = mcAtMention;

  if (history.startPrice && history.startPrice > 0 && priceAtMention > 0) {
    const supply = mcAtMention / priceAtMention;
    hStart = history.startPrice * supply;
    hMax = (history.maxPrice && history.maxPrice > 0) ? history.maxPrice * supply : hStart;
    hMin = (history.minPrice && history.minPrice > 0) ? history.minPrice * supply : hStart;
  }

  const entry: MentionEntry = {
    tokenKey,
    altKey: context?.altKey,
    timestamp: messageDateMs || Date.now(),
    mcAtMention: hStart,
    priceAtMention: history.startPrice || priceAtMention,
    chatTitle: context?.chatTitle,
    chatId: context?.chatId,
    senderName: context?.senderName,
    senderId: context?.senderId,
    messageId: context?.messageId,
    messageDate: context?.messageDate,
    historyStartMcap: hStart,
    historyMaxMcap: hMax,
    historyMinMcap: hMin,
    historyVolume: history.volume,
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_MENTIONS, 'readwrite');
    const store = tx.objectStore(STORE_MENTIONS);
    store.add(entry);

    if (context?.altKey && context.altKey !== tokenKey) {
      const altEntry: MentionEntry = {
        ...entry,
        tokenKey: context.altKey,
        altKey: tokenKey,
      };
      store.add(altEntry);
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Get all mentions for a token (across both primary key and alias key, sorted ascending)
export async function getMentions(tokenKey: string, altKey?: string): Promise<MentionEntry[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_MENTIONS, 'readonly');
    const store = tx.objectStore(STORE_MENTIONS);
    const index = store.index('byToken');
    const req1 = index.getAll(tokenKey);
    let req2: IDBRequest<MentionEntry[]> | undefined;

    if (altKey && altKey !== tokenKey) {
      req2 = index.getAll(altKey);
    }

    tx.oncomplete = () => {
      const list1 = (req1.result || []) as MentionEntry[];
      const list2 = (req2?.result || []);
      const combined = [...list1, ...list2];

      const seen = new Set<string>();
      const deduped: MentionEntry[] = [];

      for (const m of combined) {
        const uniqueKey = m.chatId && m.messageId
          ? `${m.chatId}_${m.messageId}`
          : `${m.senderId || ''}_${m.timestamp}`;
        if (!seen.has(uniqueKey)) {
          seen.add(uniqueKey);
          deduped.push(m);
        }
      }

      deduped.sort((a, b) => a.timestamp - b.timestamp);
      resolve(deduped);
    };
    tx.onerror = () => reject(tx.error);
  });
}

// Get the first mention of a token across all chats (the earliest caller)
export async function getFirstCaller(tokenKey: string, altKey?: string): Promise<MentionEntry | undefined> {
  const mentions = await getMentions(tokenKey, altKey);
  return mentions[0];
}

// Get the first mention of a token in a specific chat
export async function getFirstCallerInChat(
  tokenKey: string,
  chatId: string,
  altKey?: string,
): Promise<MentionEntry | undefined> {
  const mentions = await getMentions(tokenKey, altKey);
  return mentions.find((m) => m.chatId === chatId);
}
